import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Phone, Lock, User, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TenantLogin = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isRegister) {
        // Register
        await axios.post(`${BACKEND_URL}/api/tenant/register`, formData);
        setSuccess('Registrasi berhasil! Silakan login.');
        setIsRegister(false);
        setFormData({ ...formData, password: '' });
      } else {
        // Login
        const response = await axios.post(`${BACKEND_URL}/api/tenant/login`, formData);
        
        localStorage.setItem('tenantToken', response.data.access_token);
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
        
        navigate('/tenant/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Terjadi kesalahan. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-isk-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <img src="/isk-logo.png" alt="ISK" className="h-16 w-auto mx-auto mb-2" />
          <p className="text-isk-muted">Portal Tenant</p>
        </div>

        {/* Login Card */}
        <div className="glass-card rounded-2xl p-8 shadow-xl">
          {/* Back to Home */}
          <Link
            to="/"
            className="inline-flex items-center text-isk-muted hover:text-isk-teal mb-6 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Beranda
          </Link>

          <h2 className="text-2xl font-bold text-isk-text mb-6">Login Tenant</h2>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
              {success}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-isk-text font-semibold">Nomor HP</Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-isk-muted" />
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="08xxxxxxxxxx"
                  className="pl-12 py-6 glass-input border-2 focus:border-isk-teal rounded-xl text-lg"
                  data-testid="tenant-phone"
                />
              </div>
              {false && (
                <p className="text-xs text-isk-muted">
                  Gunakan nomor HP yang terdaftar saat check-in
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-isk-text font-semibold">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-isk-muted" />
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Masukkan password"
                  className="pl-12 pr-12 py-6 glass-input border-2 focus:border-isk-teal rounded-xl text-lg"
                  data-testid="tenant-password"
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPw(s => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full py-6 text-lg font-semibold shadow-lg"
              data-testid="tenant-submit"
            >
              {loading ? 'Loading...' : 'Masuk'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-isk-muted">
              Belum punya akun?{' '}
              <Link to="/daftar" className="text-isk-teal hover:underline font-semibold">
                Daftar Sekarang →
              </Link>
            </p>
          </div>
        </div>

        {/* Admin/Owner Login Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="text-sm text-isk-muted hover:text-isk-teal transition-colors"
          >
            Login sebagai Admin/Owner →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TenantLogin;
